import { PropsWithChildren, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Container } from '@mui/material';
import { selectMeta } from '../../../redux/meta';
import { numberOfQuestions } from '../../../redux/meta-config/screens';
import { Icon, IconPropTypes } from '../../icon/icon';
import { AssessmentHeader } from './assessment-header';
import { AppHeader, HeaderVariantName } from '../../reusable';
import { useLDFlags } from '../../providers/ld-flags';
import { AssessmentSections, Screens } from '../../../redux/types';
import { theme } from '../../../styles/theme';
import { selectPatient } from '../../../redux/assessment';
import { selectIsGuardian } from '../../../redux/session';

type PropTypes = PropsWithChildren<{
  onBack?: () => void;
  showPrivacyPolicy?: boolean;
}>;

const SECTION_HEADERS: Record<AssessmentSections, HeaderVariantName> = {
  intro: 'sand',
  onboarding: 'saffron',
  clinical: 'pistachio',
  booking: 'sky',
};

/* default is 'text.primary' */
export const TITLE_COLORS: Record<AssessmentSections, string> = {
  intro: 'primary.main',
  onboarding: 'primary.main',
  clinical: 'pistachio.title',
  booking: 'sky.title',
};

/* default is backgroundColor: 'background.default' */
const BG_STYLES = {
  [Screens.IntroScreenOne]: {
    backgroundColor: 'secondary.main',
    backgroundImage: 'url("/SaffronTalkLinesCrop.svg")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'bottom right',
    backgroundSize: 'clamp(540px, 70%, 900px)',
  },
  [Screens.MatchLoading]: {
    backgroundImage: 'url("/brain-pattern-lite.svg")',
    backgroundRepeat: 'repeat-x',
    backgroundPosition: 'bottom',
    backgroundSize: 'auto clamp(250px, 20%, 500px)',
  },
};

const SCREEN_ICONS: Partial<Record<Screens, IconPropTypes>> = {
  [Screens.MatchLoading]: {
    icon: 'UserDoctor',
    styleType: 'duotone',
    htmlColor: theme.palette.sky.dark,
    fontSize: 'large',
  },
};

const SCREEN_TITLE_OVERRIDE_LIST: Partial<Record<Screens, string>> = {
  [Screens.RV_SecondaryReason]:
    'Are you currently experiencing any of the following?',
  [Screens.RV_PsychiatricImpatient]:
    'In the past 6 months, have you had to stay overnight in a hospital, clinic, or other inpatient setting because of a mental health concern?',
  [Screens.RV_UseOfDrugs]: 'How often do you typically use drugs?',
  [Screens.RV_DrinkingAlcohol]: 'How often do you typically drink alcohol?',
  [Screens.MI_HaveAppointment]: 'Have you already scheduled your first visit?',
  [Screens.Finish]: 'Let’s talk about your matches',
};

const SCREEN_SUBTITLE_OVERRIDE_LIST: Partial<Record<Screens, string>> = {
  [Screens.Finish]:
    'Based on your preferences and our doctors’ availability, it’s best to do this last step over the phone.',
};

export const ReduxFlowContainer = ({ onBack, children }: PropTypes) => {
  const { shouldUseIntakeV2Point1, shouldShowMatchLoadingScreen } =
    useLDFlags();
  const meta = useSelector(selectMeta);
  const patient = useSelector(selectPatient);
  const isGuardian = useSelector(selectIsGuardian);
  const showProgressBar = !meta.present.review && Boolean(meta.present.step);
  const completedQuestions = meta.past.filter(s => s.step).length;
  const stepProgress =
    ((completedQuestions + 1) / (numberOfQuestions + 1)) * 100; // offset by 1 so first screen shows progress
  const { isSectionIntro = false } = meta.present;
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');

  // Hacky, but unfortunately necessary for intake 2.1 feature flag
  const overrideAssessmentSection = [Screens.CollectDemographics];
  const assessmentSection =
    !shouldUseIntakeV2Point1 &&
    overrideAssessmentSection.includes(meta.present.screen)
      ? AssessmentSections.Onboarding
      : meta.present.assessmentSection;

  SCREEN_TITLE_OVERRIDE_LIST[Screens.RV_KillingYourself] = isGuardian
    ? `In the past 6 months, has ${patient.preferredName} attempted suicide or planned to?`
    : 'In the past 6 months, have you attempted suicide or planned to?';

  if (isGuardian && shouldUseIntakeV2Point1) {
    SCREEN_TITLE_OVERRIDE_LIST[
      Screens.RV_SpecialtyCare
    ] = `Do any of the following apply to ${patient.preferredName}?`;
  }

  const headerVariant =
    assessmentSection === AssessmentSections.Intro && isSectionIntro
      ? 'saffron'
      : SECTION_HEADERS[assessmentSection];

  const showHeaderBgImage =
    assessmentSection !== AssessmentSections.Intro || !isSectionIntro;

  const titleColor = assessmentSection
    ? TITLE_COLORS[assessmentSection]
    : 'text.primary';

  const backgroundStyle = BG_STYLES[meta.present.screen] || {};

  const iconProps = shouldShowMatchLoadingScreen // Note: Scoped to this FF because we only have the match loading screen icon. If we add others, then we'll need to conditionally include icons in the array as necessary.
    ? SCREEN_ICONS[meta.present.screen] ?? undefined
    : undefined;

  useEffect(() => {
    if (meta.present) {
      const screenTitleOverride =
        shouldUseIntakeV2Point1 &&
        SCREEN_TITLE_OVERRIDE_LIST[meta.present.screen];
      setTitle(screenTitleOverride || meta.present.label);
      const screenSubtitleOverride =
        shouldUseIntakeV2Point1 &&
        SCREEN_SUBTITLE_OVERRIDE_LIST[meta.present.screen];
      setSubtitle(screenSubtitleOverride || meta.present.subLabel);
    }
  }, [meta.present]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        ...backgroundStyle,
      }}
    >
      <AppHeader
        variant={headerVariant}
        onBack={onBack}
        showProgressBar={showProgressBar}
        stepProgress={stepProgress}
        showBgImage={showHeaderBgImage}
      />
      <Container
        maxWidth="sm"
        sx={{
          position: 'relative',
          flex: 1,
          mb: 3,
        }}
      >
        {title && (
          <AssessmentHeader
            icon={iconProps && <Icon {...iconProps} />}
            title={title}
            subtitle={subtitle}
            titleColor={titleColor}
            screen={meta.present.screen}
          />
        )}
        {children}
      </Container>
    </Box>
  );
};
